import { Box, Typography, Tooltip } from "@material-ui/core";
import { Eye, FilmSlate, RadioButton } from "phosphor-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import SequencerSeekbar from "../../Utilities/Svg/SequencerSeekbar";

const CANVAS_WIDTH = 1000;

// Heights that align exactly with the three sidebar boxes
const getAlignedHeights = (containerHeight = 200) => {
  // Align heights exactly with the three sidebar boxes:
  // 1. Scale height matches top box: 30px
  // 2. Thumbnail height matches middle box: calc(100% - 70px) = containerHeight - 70px
  // 3. Status height matches bottom box: 32px

  const scaleHeight = 25;
  const statusHeight = 32;
  // Calculate thumbnail height to fill remaining space (like the middle box)
  const thumbHeight = Math.max(
    80,
    containerHeight - scaleHeight - statusHeight - 8
  ); // 8px for gaps

  return {
    scaleHeight,
    thumbHeight,
    statusHeight,
  };
};

function drawScale(
  ctx,
  totalDuration,
  pixelsPerSecond,
  canvasWidth,
  scaleHeight
) {
  ctx.clearRect(0, 0, canvasWidth, scaleHeight);
  ctx.strokeStyle = "#606479";
  ctx.fillStyle = "#606479";
  ctx.font = "12px Arial";
  for (let s = 0; s <= totalDuration; s++) {
    const x = s * pixelsPerSecond;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    // Draw a long bar every 10 seconds, short bar otherwise
    ctx.lineTo(
      x,
      s % 10 === 0 ? scaleHeight * 0.5 : Math.min(10, scaleHeight * 0.36)
    );
    ctx.stroke();
    // Show time marking on every 10 seconds
    if (s % 5 === 0) {
      ctx.fillText(`${s}s`, x - 10, scaleHeight - 2);
    }
  }
}

function drawThumbnails(
  ctx,
  videoDurations,
  pixelsPerSecond,
  thumbnails,
  canvasWidth,
  thumbHeight,
  shotsLineUp
) {
  ctx.clearRect(0, 0, canvasWidth, thumbHeight);
  let x = 0;
  const containerPadding = 4; // 4px padding inside container
  const topPadding = Math.max(3, thumbHeight * 0.06); // Responsive top padding
  const bottomPadding = Math.max(2, thumbHeight * 0.026); // Responsive bottom padding
  const nameGap = Math.max(3, thumbHeight * 0.043); // Responsive gap between name and thumbnail
  const borderRadius = 5; // 5px border radius
  const nameHeight = Math.max(12, thumbHeight * 0.128); // Responsive height for the name section
  const containerGap = 1; // 2px gap between containers

  videoDurations.forEach((duration, idx) => {
    const width = duration * pixelsPerSecond;
    const containerHeight = thumbHeight - topPadding - bottomPadding;
    const thumbnailHeight =
      containerHeight - nameHeight - nameGap - bottomPadding; // Account for bottom padding

    // Draw container background with border radius - 2px gap between containers
    ctx.save();
    ctx.fillStyle = "#463F88"; // Container background color
    ctx.beginPath();
    ctx.moveTo(x + borderRadius, topPadding);
    ctx.lineTo(x + width - containerGap - borderRadius, topPadding);
    ctx.quadraticCurveTo(
      x + width - containerGap,
      topPadding,
      x + width - containerGap,
      topPadding + borderRadius
    );
    ctx.lineTo(
      x + width - containerGap,
      topPadding + containerHeight - borderRadius
    );
    ctx.quadraticCurveTo(
      x + width - containerGap,
      topPadding + containerHeight,
      x + width - containerGap - borderRadius,
      topPadding + containerHeight
    );
    ctx.lineTo(x + borderRadius, topPadding + containerHeight);
    ctx.quadraticCurveTo(
      x,
      topPadding + containerHeight,
      x,
      topPadding + containerHeight - borderRadius
    );
    ctx.lineTo(x, topPadding + borderRadius);
    ctx.quadraticCurveTo(x, topPadding, x + borderRadius, topPadding);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Calculate thumbnail dimensions first
    const thumbnailY = topPadding + nameHeight + nameGap;
    const thumbnailWidth = width - containerGap - containerPadding * 2; // Account for left and right padding
    const thumbnailX = x + containerPadding; // Start inside the padding

    // Draw shot name with ellipsis
    ctx.save();
    ctx.font = "12px Arial";
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";

    const shotName = `Shot ${shotsLineUp[idx]?.shotVersion?.name}`;
    const maxTextWidth = thumbnailWidth; // Available width for text
    let displayText = shotName;

    // Check if text is too wide and truncate with ellipsis
    if (ctx.measureText(shotName).width > maxTextWidth) {
      // Binary search to find the longest text that fits
      let start = 0;
      let end = shotName.length;
      while (start < end) {
        const mid = Math.floor((start + end + 1) / 2);
        const testText = shotName.substring(0, mid) + "...";
        if (ctx.measureText(testText).width <= maxTextWidth) {
          start = mid;
        } else {
          end = mid - 1;
        }
      }
      displayText = shotName.substring(0, start) + "...";
    }

    ctx.fillText(
      displayText,
      x + containerPadding,
      topPadding + containerPadding
    );
    ctx.restore();

    // Draw thumbnail image or colored rect with border radius

    if (thumbnails[idx]) {
      // Draw thumbnail image with border radius, fitted as content
      ctx.save();
      ctx.beginPath();
      const innerRadius = 3; // Slightly smaller radius for inner content
      ctx.moveTo(thumbnailX + innerRadius, thumbnailY);
      ctx.lineTo(thumbnailX + thumbnailWidth - innerRadius, thumbnailY);
      ctx.quadraticCurveTo(
        thumbnailX + thumbnailWidth,
        thumbnailY,
        thumbnailX + thumbnailWidth,
        thumbnailY + innerRadius
      );
      ctx.lineTo(
        thumbnailX + thumbnailWidth,
        thumbnailY + thumbnailHeight - innerRadius
      );
      ctx.quadraticCurveTo(
        thumbnailX + thumbnailWidth,
        thumbnailY + thumbnailHeight,
        thumbnailX + thumbnailWidth - innerRadius,
        thumbnailY + thumbnailHeight
      );
      ctx.lineTo(thumbnailX + innerRadius, thumbnailY + thumbnailHeight);
      ctx.quadraticCurveTo(
        thumbnailX,
        thumbnailY + thumbnailHeight,
        thumbnailX,
        thumbnailY + thumbnailHeight - innerRadius
      );
      ctx.lineTo(thumbnailX, thumbnailY + innerRadius);
      ctx.quadraticCurveTo(
        thumbnailX,
        thumbnailY,
        thumbnailX + innerRadius,
        thumbnailY
      );
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(
        thumbnails[idx],
        thumbnailX,
        thumbnailY,
        thumbnailWidth,
        thumbnailHeight
      );
      ctx.restore();
    } else {
      // Draw colored rectangle with border radius as placeholder
      ctx.save();
      ctx.fillStyle = "#2A2F42"; // Darker placeholder color to differentiate from container
      ctx.beginPath();
      const innerRadius = 3; // Slightly smaller radius for inner content
      ctx.moveTo(thumbnailX + innerRadius, thumbnailY);
      ctx.lineTo(thumbnailX + thumbnailWidth - innerRadius, thumbnailY);
      ctx.quadraticCurveTo(
        thumbnailX + thumbnailWidth,
        thumbnailY,
        thumbnailX + thumbnailWidth,
        thumbnailY + innerRadius
      );
      ctx.lineTo(
        thumbnailX + thumbnailWidth,
        thumbnailY + thumbnailHeight - innerRadius
      );
      ctx.quadraticCurveTo(
        thumbnailX + thumbnailWidth,
        thumbnailY + thumbnailHeight,
        thumbnailX + thumbnailWidth - innerRadius,
        thumbnailY + thumbnailHeight
      );
      ctx.lineTo(thumbnailX + innerRadius, thumbnailY + thumbnailHeight);
      ctx.quadraticCurveTo(
        thumbnailX,
        thumbnailY + thumbnailHeight,
        thumbnailX,
        thumbnailY + thumbnailHeight - innerRadius
      );
      ctx.lineTo(thumbnailX, thumbnailY + innerRadius);
      ctx.quadraticCurveTo(
        thumbnailX,
        thumbnailY,
        thumbnailX + innerRadius,
        thumbnailY
      );
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    x += width + containerGap; // Add gap between containers
  });
}

const SequencerMixTimeline = ({
  videoDurations,
  currentTime,
  setCurrentTime,
  thumbnails,
  setSeekToTime,
  setIsVideoPlaying,
  shotsLineUp,
}) => {
  const [scaleWidth, setScaleWidth] = useState(1000); // Make scaleWidth stateful
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(200);
  const { scaleHeight, thumbHeight, statusHeight } =
    getAlignedHeights(containerHeight);
  const totalDuration = videoDurations.reduce((a, b) => a + b, 0);
  const pixelsPerSecond = scaleWidth / totalDuration;
  const scaleRef = useRef(null);
  const thumbRef = useRef(null);
  const [thumbImages, setThumbImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Clamp scaleWidth to a minimum based on container or content
  const minScaleWidth = Math.max(400, totalDuration * 10); // 10px per second minimum

  // Monitor container height for responsive heights
  useEffect(() => {
    const updateContainerHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    // Initial height calculation
    updateContainerHeight();

    // Add resize listener
    window.addEventListener("resize", updateContainerHeight);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateContainerHeight);
    };
  }, []);

  // Handle mouse wheel to zoom in/out
  const handleWheel = (e) => {
    if (e.ctrlKey) {
      // When Control is pressed, allow normal horizontal scrolling
      return;
    } else {
      // When Control is not pressed, handle zooming
      setScaleWidth((prev) => {
        let next = prev + (e.deltaY < 0 ? 100 : -100);
        if (next < minScaleWidth) next = minScaleWidth;
        if (next > 5000) next = 5000;
        return next;
      });
    }
  };

  useEffect(() => {
    // Preload images
    Promise.all(
      thumbnails.map(
        (url) =>
          new Promise((resolve) => {
            const img = new window.Image();
            img.src = url;
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
          })
      )
    ).then(setThumbImages);
  }, [thumbnails]);

  useLayoutEffect(() => {
    if (!scaleRef.current || totalDuration <= 0) return;
    const ctx = scaleRef.current.getContext("2d");
    drawScale(ctx, totalDuration, pixelsPerSecond, scaleWidth, scaleHeight);
  }, [totalDuration, pixelsPerSecond, scaleWidth, scaleHeight, shotsLineUp.length]);

  useLayoutEffect(() => {
    if (!thumbRef.current || !shotsLineUp.length || !videoDurations.length) return;
    const ctx = thumbRef.current.getContext("2d");
    drawThumbnails(
      ctx,
      videoDurations,
      pixelsPerSecond,
      thumbImages,
      scaleWidth,
      thumbHeight,
      shotsLineUp
    );
  }, [
    videoDurations,
    pixelsPerSecond,
    thumbImages,
    scaleWidth,
    thumbHeight,
    shotsLineUp,
  ]);

  // Helper to get time from mouse X position
  const getTimeFromX = (clientX, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scrollContainer = canvas.parentElement;
    const scrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;

    // Add scroll position to the clientX to get the true position
    let x = clientX - rect.left + scrollLeft;
    x = Math.max(0, Math.min(x, scaleWidth));
    const time = (x / scaleWidth) * totalDuration;
    return time;
  };

  // Mouse event handlers
  const handlePointerDown = (e) => {
    setIsDragging(true);
    const canvas = e.target;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setCurrentTime(getTimeFromX(clientX, canvas));
    console.log("handle pointer down runs");
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const canvas = e.target;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setCurrentTime(getTimeFromX(clientX, canvas));
    console.log("handle pointer move runs");
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;
    const canvas = e.target;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setIsVideoPlaying(false);
    setSeekToTime(getTimeFromX(clientX, canvas));
    setCurrentTime(getTimeFromX(clientX, canvas));
    // console.log("this is pointer up", getTimeFromX(clientX, canvas));
    console.log("handle pointer up runs");
    setIsDragging(false);
  };

  // Attach events to both canvases
  useEffect(() => {
    const canvases = [scaleRef.current, thumbRef.current];
    canvases.forEach((canvas) => {
      if (!canvas) return;
      canvas.addEventListener("mousedown", handlePointerDown);
      canvas.addEventListener("mousemove", handlePointerMove);
      window.addEventListener("mouseup", handlePointerUp);

      // Touch support
      // Set passive to true for wheel events
      canvas.addEventListener("wheel", handleWheel, { passive: true });
      canvas.addEventListener("touchstart", handlePointerDown);
      canvas.addEventListener("touchmove", handlePointerMove);
      window.addEventListener("touchend", handlePointerUp);
    });
    return () => {
      canvases.forEach((canvas) => {
        if (!canvas) return;
        canvas.removeEventListener("mousedown", handlePointerDown);
        canvas.removeEventListener("mousemove", handlePointerMove);
        window.removeEventListener("mouseup", handlePointerUp);
        // Match the passive setting in the cleanup
        canvas.removeEventListener("wheel", handleWheel, { passive: true });

        canvas.removeEventListener("touchstart", handlePointerDown);
        canvas.removeEventListener("touchmove", handlePointerMove);
        window.removeEventListener("touchend", handlePointerUp);
      });
    };
  });

  return (
    <>
      <Box
        style={{
          width: "80px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          borderRadius: "10px",
        }}
      >
        <Box
          style={{
            height: "30px",
            backgroundColor: "#1B1D28",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            padding: "4px 8px",
            justifyContent: "space-between",
            borderTopLeftRadius: "5px",
          }}
        >
          <Typography style={{ fontSize: "12px" }} color="#606479">
            Seconds
          </Typography>
        </Box>
        <Box
          style={{
            height: "calc(100% - 70px)",
            backgroundColor: "#1B1D28",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            padding: "4px 8px",
            justifyContent: "center",
          }}
        >
          <FilmSlate size={20} color="#606479" />
        </Box>
        <Box
          style={{
            height: "32px",
            backgroundColor: "#1B1D28",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            padding: "4px 8px",
            justifyContent: "center",
            borderBottomLeftRadius: "5px",
          }}
        >
          <RadioButton size={20} color="#606479" />
        </Box>
      </Box>
      <Box
        ref={containerRef}
        style={{
          height: "100%",
          width: "calc(100vw - 270px)",
          position: "relative",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          backgroundColor: "#1B1D28",
        }}
        onWheel={(e) => {
          e.stopPropagation();
          handleWheel(e);
        }}
      >
        {shotsLineUp.length === 0 && (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography style={{ fontSize: "14px", color: "#606479" }}>
              No shots in selected sequence
            </Typography>
          </div>
        )}
        {shotsLineUp.length > 0 && totalDuration > 0 && (
          <div style={{ width: scaleWidth, minWidth: minScaleWidth }}>
            {/* Seekbar */}
            <Box
              style={{
                position: "absolute",
                left: `${currentTime * pixelsPerSecond}px`,
                top: 0,
                height: "100%",
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              <SequencerSeekbar height={thumbHeight + scaleHeight} />
            </Box>
            <canvas
              ref={scaleRef}
              width={scaleWidth + (videoDurations.length - 1) * 1}
              height={scaleHeight}
              style={{
                cursor: "pointer",
                display: "block",
                marginBottom: "4px",
              }}
            />
            <canvas
              ref={thumbRef}
              width={scaleWidth + (videoDurations.length - 1) * 1}
              height={thumbHeight}
              style={{
                cursor: "pointer",
                display: "block",
                marginBottom: "4px",
              }}
            />
            {/* Status bar below thumbnails */}
            <div
              style={{
                width: scaleWidth + (videoDurations.length - 1) * 1,
                // minWidth: minScaleWidth,
                height: statusHeight,
                position: "relative",
                overflow: "hidden", // Prevent status bars from extending beyond container
              }}
            >
              {videoDurations.map((duration, idx) => {
                const width = duration * pixelsPerSecond;
                const containerGap = 1; // Same gap as used in thumbnail drawing

                // Calculate x position exactly like in canvas drawing
                let x = 0;
                for (let i = 0; i < idx; i++) {
                  const prevWidth = videoDurations[i] * pixelsPerSecond;
                  x += prevWidth + containerGap; // Match canvas: x += width + containerGap
                }

                const statusColor = shotsLineUp[idx]?.shotVersion?.status
                  ? `rgb(${shotsLineUp[idx].shotVersion.status.color})`
                  : "#252A38";

                return (
                  <Tooltip title={`Status ${idx + 1}`}>
                    <div
                      key={idx}
                      style={{
                        position: "absolute",
                        left: x,
                        top: 0,
                        width: width - containerGap,
                        height: "100%",
                        background: statusColor,
                        textAlign: "center",
                        paddingTop: "7px",
                        fontSize: 12,
                        color: "#fff",
                        padding: "4px",
                        borderRight: "1px solid #252A38",
                        borderRadius: "5px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        boxSizing: "border-box",
                      }}
                    >
                      {shotsLineUp[idx]?.shotVersion?.status?.name
                        ? shotsLineUp[idx].shotVersion.status.name
                        : "No Status"}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}
      </Box>
    </>
  );
};

export default SequencerMixTimeline;
